import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';

import { SetOnlineInterface } from '@core/models/invite.model';
import { MessageModel } from '@core/models/message.model';
import { TaskModel } from '@core/models/task.model';
import * as fromRoot from '@core/redux';
import * as InviteActions from '@core/redux/invite/invite.actions';
import * as MessageActions from '@core/redux/message/message.actions';
import * as TaskActions from '@core/redux/task/task.actions';
import { BadgeService } from '@core/services/badge.service';
import { TaskQueryService } from '@core/services/query/task.query.service';
import { getUserId } from '@core/utils/getUserId';
import { Store } from '@ngrx/store';
import { ImagePreviewPipe } from '@ux/pipes/image-preview.pipe';
import { Socket } from 'ngx-socket-io';
import * as visibility from 'visibilityjs';

@Injectable({
  providedIn: 'root',
})
export class SocketService {
  private _taskId;
  private _projectId;

  constructor(
    private socket: Socket,
    private store: Store<fromRoot.State>,
    private zone: NgZone,
    private imagePreviewPipe: ImagePreviewPipe,
    private router: Router,
    private taskQueryService: TaskQueryService,
    private badgeService: BadgeService
  ) {
    this.socket.on('connect', () => {
      console.log('Socket connected');
    });

    this.socket.on('disconnect', reason => {
      console.log('Socket disconnected:', reason);
    });

    this.socket.on('connect_error', error => {
      console.error('Socket connection error:', error);
    });

    this.socket.on('error', error => {
      console.error('Socket error:', error);
    });

    this.socket.on('authenticated', _ => {
      console.log('Socket authenticated');
    });

    this.socket.on('connect_timeout', timeout => {
      console.error('Socket connection timeout:', timeout);
    });

    this.socket.on('reconnect', attemptNumber => {
      console.log('Socket reconnected after', attemptNumber, 'attempts');
    });

    this.socket.on('reconnect_error', error => {
      console.error('Socket reconnect error:', error);
    });

    this.socket.on('reconnect_failed', () => {
      console.error('Socket reconnect failed');
    });

    this.socket.on('connect', () => {
      console.log('Socket connected to namespace');
    });

    this.socket.on('[Task] Refresh Chats', _ => {
      this.store.dispatch(new TaskActions.SearchNotificationAction());
    });

    // Добавить или обновить задачу в сайдбаре чата
    this.socket.on('[Chat] Update Task', data => {
      const task = new TaskModel().setAttributes(data);
      this.store.dispatch(new TaskActions.UpdateNotificationAction(task));
    });

    this.socket.on('[Task] Refresh', _ => {
      this.taskQueryService.reload();
    });

    this.socket.on(`[Message] Added`, data => {
      const message = new MessageModel().setAttributes(data);

      const userId = getUserId();
      if (message.authorId !== userId) {
        if (visibility.hidden() && message.followers.indexOf(userId) > -1) {
          let imageUrl = null;

          if (message.author.avatar) {
            imageUrl = this.imagePreviewPipe.transform(message.author.avatar, 150, 150, true);
          }

          const notification = new Notification(`${message.author.firstName} ${message.author.lastName}`, {
            body: message.message,
            icon: imageUrl,
          });

          notification.onclick = _ => {
            this.zone.run(() => {
              this.router.navigate([
                '/cabinet',
                message.projectId,
                'load',
                { outlets: { task: ['view', message.taskId] } },
              ]);
            });
          };
        }

        if (data.taskId === this._taskId) {
          this.store.dispatch(new MessageActions.CreateSuccessAction(message));
        }
      }
    });

    this.socket.on(`[Message] Updated`, data => {
      const message = new MessageModel().setAttributes(data);

      if (data.taskId === this._taskId) {
        this.store.dispatch(new MessageActions.SaveSuccessAction(message));
      }
    });

    this.socket.on(`[Message] Deleted`, data => {
      const message = new MessageModel().setAttributes(data);

      if (message.authorId !== getUserId()) {
        if (data.taskId === this._taskId) {
          this.store.dispatch(new MessageActions.DeleteSuccessAction(message));
        }
      }
    });
  }

  listen(token) {
    this.socket.emit('authentication', { accessToken: token });
  }

  listenOnline(projectId) {
    if (this._projectId && this._projectId !== projectId) {
      this.socket.removeListener(`[User] Online:${this._projectId}`);
    }
    this._projectId = projectId;
    this.socket.on(`[User] Online:${projectId}`, (data: SetOnlineInterface[]) => {
      this.store.dispatch(new InviteActions.SetOnlineAction(data));
    });
  }

  openTask(taskId) {
    this.closeTask();
    this._taskId = taskId;

    // @todo: переделать
    // Очищаем бейдж при открытии задачи
    this.store
      .select(fromRoot.getNotificationsTask)
      .subscribe(notifications => {
        const totalCount = notifications.reduce((sum, task) => {
          // Не учитываем уведомления открытой задачи
          if (task.id === taskId) {
            return sum;
          }
          return sum + (task.countNotifications || 0);
        }, 0);
        this.badgeService.setBadgeCount(totalCount);
      })
      .unsubscribe();
  }

  closeTask() {
    this.socket.removeListener(`[Message] Added:${this._taskId}`);
  }
}
